import { intlGet } from '@/locales';
import { formatSourceName } from '@/utils/common/businessLogic';
import { doesItExist } from '@/utils/common/dataJudgment';
import type { TabPanelProps } from 'tdesign-react';
import type { ApiTypeEnum } from './data.d';
import {
  ApiSourceEnum,
  NightTimePermissionStatusEnum,
  PlaytimeStatusEnum,
  SourceEnum,
  WishlistStatusEnum,
} from './data.d';

/** 获取来源key */
export const getSourceKey = (val: string) => {
  if (doesItExist(SourceEnum.LI_PASS, val)) {
    return SourceEnum.LI_PASS;
  }
  return (val as SourceEnum) || SourceEnum.API;
};

/** 来源拼接词条 - 区分来源(获取来源国际化key) */
export const sourceIntlJson = {
  // li-pass导入联系人
  [SourceEnum.LI_PASS]: 'LI-PASS',
  // data-brain导入联系人
  [SourceEnum.DATA_BRAIN]: 'IN-GAME',
  // 文件上传联系人
  [SourceEnum.FILE]: 'FILE',
  // 单独添加联系人
  [SourceEnum.SINGLE_ADD]: 'SINGLE-ADD',
  // api导入联系人
  [SourceEnum.API]: 'API',
  // 工具导入
  [SourceEnum.TOOL]: 'TOOLS',
  // 工具导入
  [SourceEnum.TOOLS]: 'TOOLS',
  // Data Science Team
  [SourceEnum.DATA_SCIENCE_TEAM]: 'DATA-SCIENCE-TEAM',
  // LEVEL INFINITE PASS订阅
  [SourceEnum.NEWSLETTER_SIGN_UP]: 'NEWSLETTER-SIGN-UP',
  // LEVEL INFINITE PASS订阅
  [SourceEnum.SIGN_UP_FORM]: 'SIGN-UP-FORM',
  // 业务关联组内同步
  [SourceEnum.ASSOCIATE_GROUP]: 'ASSOCIATE_GROUP',
  // // 管理端内 - 标签管理列表
  // [SourceEnum.TAG_LIST]: 'TAG-LIST',
  // whatsapp
  [SourceEnum.WHATSAPP]: 'WHATSAPP',
  // 联系人详情页
  [SourceEnum.DETAILS]: 'DETAILS',
  /** 二次确认 */
  [SourceEnum.OPTIN]: 'OPTIN',
  /** 旅程 */
  [SourceEnum.JOURNEY]: 'JOURNEY',
  /** 官网招募 */
  [SourceEnum.RECRUIT]: 'RECRUIT',
  /** 测试邀请 */
  [SourceEnum.PLAYTEST_HUB]: 'PLAYTEST-HUB',
  /** Player Network：三方账号（渠道账号） */
  [SourceEnum.PN_THIRD_PARTY]: 'PN-THIRD-PARTY',
  /** Player Network：业务品牌账号(gcp studio 环境) */
  [SourceEnum.PN_GCP_LIPASS]: 'LI-PASS',
  /** Player Network：业务品牌账号(普通环境) */
  [SourceEnum.PN_LIPASS]: 'LI-PASS',
  /** 玩家验证 */
  [SourceEnum.PLAYER_VERIFICATION]: 'PLAYER_VERIFICATION',
  /** WorkBuddy */
  [SourceEnum.WORKBUDDY]: 'WORKBUDDY',
};

/** 获取国际化 对应键名 例如：XXXX.XXXX.API 或 XXXX.XXXX.LI-PASS */
export const getSourceIntlKey = (val: string) => {
  // console.log('val', val);
  // console.log('getSourceKey', getSourceKey(val));
  return sourceIntlJson[getSourceKey(val)];
};

/** 来源国际化文案输出 */
export const getSourceText = (sourceKey: string) => {
  let newSourceKey = sourceKey;
  if (
    (
      [ApiSourceEnum.API_LI_PASS, ApiSourceEnum.API_IN_GAME] as string[]
    ).includes(sourceKey)
  ) {
    newSourceKey = formatSourceName(sourceKey);
  }

  return (
    intlGet(`CONTACT.SOURCE.${getSourceIntlKey(newSourceKey)}`) || newSourceKey
  );
};

// 是否显示邮箱或者WhatsApp
export const isShowEmailOrWhatsapp = (status: number) => {
  return status === 1;
};

export const getNightTimePermissionText = (status: number) => {
  const textMap: Record<number, string> = {
    [NightTimePermissionStatusEnum.ALLOW]: intlGet(
      'CONTACT.DETAILS.NIGHT.MARKETING.VALUE.1',
    ),
    [NightTimePermissionStatusEnum.NOT_ALLOW]: intlGet(
      'CONTACT.DETAILS.NIGHT.MARKETING.VALUE.2',
    ),
    [NightTimePermissionStatusEnum.UNKNOWN]: intlGet(
      'CONTACT.DETAILS.NIGHT.MARKETING.VALUE.3',
    ),
  };
  return textMap[status] || '-';
};

export const getWishlistStatusText = (wishlistStatus: number) => {
  const textMap: Record<number, string> = {
    [WishlistStatusEnum.ADDED]: intlGet('CONTACT.DETAILS.WISHLIST.VALUE.1'),
    [WishlistStatusEnum.NOT_ADDED]: intlGet('CONTACT.DETAILS.WISHLIST.VALUE.2'),
    [WishlistStatusEnum.PRIVATE]: intlGet('CONTACT.DETAILS.PRIVATE.VALUE'),
  };
  return textMap[wishlistStatus] || '-';
};

export const getPlaytimeIcon = (playtimeStatus: number) => {
  let style = {
    iconName: '',
    color: '',
  };

  switch (playtimeStatus) {
    case PlaytimeStatusEnum.PRIVATE:
      style = {
        iconName: 'eye-close',
        color: '#999999',
      };
      break;
    case PlaytimeStatusEnum.AVAILABLE:
    case PlaytimeStatusEnum.PUBLIC:
      style = {
        iconName: 'playtime',
        color: '#00A386',
      };
      break;
    default:
      style = {
        iconName: '',
        color: '',
      };
      break;
  }
  return style;
};

export const getWishListStatusIcon = (wishlistStatus: number) => {
  const unknownStatus = {
    iconName: '',
    color: '',
  };

  const validWishlistStatuses = [
    WishlistStatusEnum.ADDED,
    WishlistStatusEnum.NOT_ADDED,
    WishlistStatusEnum.PRIVATE,
  ];

  if (!validWishlistStatuses.includes(wishlistStatus)) {
    return unknownStatus;
  }

  const statusConfig: Record<number, { iconName: string; color: string }> = {
    [WishlistStatusEnum.ADDED]: { iconName: 'wish', color: '#E3479A' },
    [WishlistStatusEnum.NOT_ADDED]: {
      iconName: 'circle-delete',
      color: '#999999',
    },
    [WishlistStatusEnum.PRIVATE]: {
      iconName: 'eye-close',
      color: '#999999',
    },
  };

  return statusConfig[wishlistStatus] || unknownStatus;
};

export const KOREA_REGION_VALUE = '410';

export const getWishlistStatusClass = (status: number) => {
  if (status === WishlistStatusEnum.PRIVATE) {
    return 'private';
  }

  const classMap: Record<number, string> = {
    [WishlistStatusEnum.ADDED]: 'added',
    [WishlistStatusEnum.NOT_ADDED]: 'not-added',
  };
  return classMap[status] || '';
};

export const getPlayTimeText = (seconds: number) => {
  if (seconds < 0) seconds = 0;

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  let formattedText = '';

  if (hours > 0) {
    formattedText += intlGet('CONTACT.DETAILS.PLAYTIME.PUBLIC.HOURS.VALUE', {
      hours,
    });
  }

  if (minutes > 0 || hours === 0) {
    formattedText += intlGet('CONTACT.DETAILS.PLAYTIME.PUBLIC.MINS.VALUE', {
      minutes,
    });
  }
  console.log('hours', hours, minutes);
  return formattedText.trim();
};

// 通用的类型安全 includes 判断
export const isApiTypeInList = (
  value: string,
  list: readonly ApiTypeEnum[],
): value is ApiTypeEnum => {
  return (list as readonly string[]).includes(value);
};
